import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface Rect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
}

export function SearchDropdown({
  open,
  anchorRef,
  testId,
  children,
}: {
  readonly open: boolean;
  readonly anchorRef: { readonly current: HTMLElement | null };
  readonly testId: string;
  readonly children: ReactNode;
}): JSX.Element | null {
  const [rect, setRect] = useState<Rect | null>(null);

  const update = useCallback((): void => {
    const el = anchorRef.current;
    if (el === null) return;
    const next = el.getBoundingClientRect();
    setRect({ left: next.left, top: next.bottom, width: next.width });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) {
      setRect(null);
      return;
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, update]);

  if (!open || rect === null) return null;

  return createPortal(
    <div
      className="pointer-events-auto fixed z-40 max-h-72 overflow-auto rounded-lg border border-line bg-raised shadow-overlay"
      role="listbox"
      style={{
        left: rect.left,
        top: rect.top + 4,
        width: rect.width,
        minWidth: rect.width,
        pointerEvents: 'auto',
      }}
      data-testid={testId}
    >
      {children}
    </div>,
    document.body,
  );
}
